import { APP_VERSION } from '../version';

interface CacheConfig {
  version: string;
  updateCheckInterval: number;
  forceUpdateOnVersionChange: boolean;
}

class CacheManager {
  private config: CacheConfig;
  private updateCheckTimer: ReturnType<typeof setInterval> | null = null;
  private updateNotified = false;
  private initialized = false;
  private swUpdateHandler: (() => void) | null = null;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      version: APP_VERSION,
      updateCheckInterval: 30000, // 30 seconds
      forceUpdateOnVersionChange: true,
      ...config
    };
  }

  /**
   * Initialize cache management
   * Safe to call multiple times — cleans up before re-initializing
   */
  async init(config: Partial<CacheConfig> = {}): Promise<void> {
    if (this.initialized) {
      this.destroy();
    }

    this.config = { ...this.config, ...config };

    if (import.meta.env.DEV) {
      console.log('[CacheManager] Skipping in development mode');
      return;
    }

    this.initialized = true;
    await this.registerServiceWorker();
    this.startUpdateChecker();
    this.checkForUpdates();
  }

  /**
   * Register service worker for PWA functionality
   * Removes previous updatefound listener to prevent accumulation
   */
  private async registerServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });

        if (this.swUpdateHandler) {
          registration.removeEventListener('updatefound', this.swUpdateHandler);
        }

        this.swUpdateHandler = () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                this.handleUpdate();
              }
            });
          }
        };

        registration.addEventListener('updatefound', this.swUpdateHandler);
      } catch (error) {
        console.warn('Service Worker registration failed:', error);
      }
    }
  }

  /**
   * Start periodic update checking
   */
  private startUpdateChecker(): void {
    this.updateCheckTimer = setInterval(() => {
      this.checkForUpdates();
    }, this.config.updateCheckInterval);
  }

  /**
   * Check for application updates by fetching version.json
   */
  async checkForUpdates(): Promise<void> {
    if (this.updateNotified) return;

    try {
      const response = await fetch(
        `${window.location.origin}/version.json?t=${Date.now()}`,
        {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const serverVersion = data.version;

        if (serverVersion && serverVersion !== this.config.version) {
          this.handleUpdate();
        }
      }
    } catch (error) {
      console.warn('Update check failed:', error);
    }
  }

  /**
   * Handle application update
   * Fires only once per session via updateNotified guard
   */
  private handleUpdate(): void {
    if (this.updateNotified) return;
    this.updateNotified = true;

    if (this.updateCheckTimer) {
      clearInterval(this.updateCheckTimer);
      this.updateCheckTimer = null;
    }

    if (this.config.forceUpdateOnVersionChange) {
      this.forceReload();
    } else {
      this.showUpdateNotification();
    }
  }

  /**
   * Force reload the application
   * Unregisters SW and busts cache to prevent stale bundle from reloading
   */
  async forceReload(): Promise<void> {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }

    await this.clearCaches();

    const url = new URL(window.location.href);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.replace(url.toString());
  }

  /**
   * Dispatch custom event for update notification
   */
  private showUpdateNotification(): void {
    const event = new CustomEvent('app-update-available', {
      detail: { version: this.config.version }
    });
    window.dispatchEvent(event);
  }

  /**
   * Clear all application caches
   */
  async clearCaches(): Promise<void> {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    }

    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('cache_') || key.startsWith('version_')) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<Record<string, number> | null> {
    if (!('caches' in window)) return null;

    const cacheNames = await caches.keys();
    const stats: Record<string, number> = {};

    for (const cacheName of cacheNames) {
      const cache = await caches.open(cacheName);
      const keys = await cache.keys();
      stats[cacheName] = keys.length;
    }

    return stats;
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.updateCheckTimer) {
      clearInterval(this.updateCheckTimer);
      this.updateCheckTimer = null;
    }
    this.initialized = false;
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Export React hook for component usage
export const useCacheManager = () => {
  return {
    clearCaches: () => cacheManager.clearCaches(),
    getCacheStats: () => cacheManager.getCacheStats(),
    checkForUpdates: () => cacheManager.checkForUpdates()
  };
};